import { MIN_CELL_DUR } from 'constants/params';
import { StoryConfig } from 'services/story.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

import { Error } from 'components/app/_helpers/app-errors.helper';
import { handleStoryConfig, TitleCreditsAction } from '../editor.slice';
import { transitionAlignment, transitionDuration } from '../editor-markers/editor-markers.helper';
import { _useConfigOrder } from './_use-config-order';

export const useEditorConfig = () => {
  const duration = useAppSelector((state) => state.audio.duration);
  const config = useAppSelector((state) => state.editor.storyConfig);
  const markers = useAppSelector((state) => state.editor.markers);
  const sortClips = _useConfigOrder();
  const dispatch = useAppDispatch();

  return (data: StoryConfig) => {
    if (!config || !markers) return;

    const _config = { ...config, ...data };

    // Ensure radio button enum values are properly converted to numbers
    // react-hook-form doesn’t apply valueAsNumber to radio buttons
    _config.selectionMethod = +_config.selectionMethod;
    _config.clipOrder = +_config.clipOrder;

    const markers_1 =
      _config.transitionTime !== config.transitionTime
        ? transitionDuration(_config.transitionTime, config.transitionTime, _config.clipTransitionAlignment, markers)
        : undefined;

    const markers_2 =
      _config.clipTransitionAlignment !== config.clipTransitionAlignment
        ? transitionAlignment(
            _config.clipTransitionAlignment,
            config.clipTransitionAlignment,
            _config.transitionTime,
            markers_1 ?? markers
          )
        : undefined;

    const storyBoard =
      _config.clipOrder !== config.clipOrder ? { storyBoard: sortClips(_config.clipOrder) } : undefined;

    if (!validator(markers_2 ?? markers_1, duration)) return;

    dispatch(
      handleStoryConfig({
        data: {
          titleCreditsAction: getTitleCreditsAction(_config, config),
          markers: markers_2 ?? markers_1,
          storyConfig: _config,
          storyBoard,
        },
        backupParams: { title: 'Story Config' },
      })
    );
  };
};

/* H E L P E R */

const getTitleCreditsAction = (curr: StoryConfig, prev: StoryConfig): TitleCreditsAction | undefined => {
  const titleChanged = curr.titleText !== prev.titleText;
  const creditsChanged = curr.creditsText !== prev.creditsText;
  const aspectRatioChanged = curr.aspectRatio !== prev.aspectRatio;
  if (aspectRatioChanged) return 'both';
  if (titleChanged && creditsChanged) return 'both';
  if (titleChanged) return 'title';
  if (creditsChanged) return 'credits';
};

const validator = (markers: MarkerId[] | undefined, duration: Seconds | null) => {
  if (!markers || !duration) return true;
  if (markers[0] <= MIN_CELL_DUR) {
    Error.tooSmallRegion('first');
    return false;
  }
  if (duration - markers[markers.length - 1] <= MIN_CELL_DUR) {
    Error.tooSmallRegion('last');
    return false;
  }
  return true;
};
